import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, Store, Tags } from 'lucide-react';
import { useTiktokWorkspaceAuth } from '@/tiktok-platform/context/TiktokWorkspaceAuthContext';
import TiktokGateSelect from '@/tiktok-platform/components/TiktokGateSelect';
import {
  TIKTOK_ACTIVE_SHOP_STORAGE_KEY,
  TIKTOK_SHOP_TYPES,
  buildTiktokPath,
  normalizeTiktokShopType,
  trackForShopType,
} from '@/tiktok-platform/constants/tiktokRoutes';
import { IPE_WORKBENCH_PATH } from '@/config/ipeRoutes';
import {
  listAuthorizedTiktokShops,
} from '@/tiktok-platform/utils/tiktokShopAuthGate';
import { gateCopyForTiktokShopType } from '@/tiktok-platform/utils/tiktokGateCopy';
import { localizedShopTypeLabel } from '@/i18n/shopTypeText';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
} from '@/utils/platformShopScope';
import '@/styles/tiktok-platform/tiktok-gate.css';

const GATE_HERO = `${process.env.PUBLIC_URL || ''}/tiktok-gate/hero.png?v=3`;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function readStoredShopId() {
  try {
    return localStorage.getItem(TIKTOK_ACTIVE_SHOP_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function persistActiveShopId(shopId) {
  try {
    if (shopId) {
      localStorage.setItem(TIKTOK_ACTIVE_SHOP_STORAGE_KEY, String(shopId));
    } else {
      localStorage.removeItem(TIKTOK_ACTIVE_SHOP_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

function resolveRedirect(searchParams, locationState, shopType) {
  const fromQuery = searchParams.get('redirect');
  if (fromQuery) return fromQuery;
  if (locationState?.redirect) return locationState.redirect;
  const track = trackForShopType(shopType) || 'crossborder';
  return buildTiktokPath(track, 'home');
}

/** 门禁店铺选项：仅展示名称与店铺类型，不带授权槽摘要 */
function shopOptionLabel(shop, t) {
  const name = String(shop?.shopName || '').trim() || t('tiktokGate.shopFallback', { id: shop?.id });
  const type = localizedShopTypeLabel(t, normalizeTiktokShopType(shop?.shopType));
  return type ? t('tiktokGate.shopOption', { name, type }) : name;
}

function useGateStoryMotion(headlineText, highlightCount) {
  const [headline, setHeadline] = useState('');
  const [showLead, setShowLead] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [typingDone, setTypingDone] = useState(false);
  const safeCount = Math.max(1, Number(highlightCount) || 1);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setHeadline(headlineText);
      setShowLead(true);
      setVisibleCount(safeCount);
      setTypingDone(true);
      return undefined;
    }

    let cancelled = false;
    let charIndex = 0;
    let typeTimer;
    let leadTimer;
    let revealTimer;
    let cycleTimer;

    setHeadline('');
    setShowLead(false);
    setVisibleCount(0);
    setActiveIndex(0);
    setTypingDone(false);

    const typeNext = () => {
      if (cancelled) return;
      charIndex += 1;
      setHeadline(headlineText.slice(0, charIndex));
      if (charIndex < headlineText.length) {
        typeTimer = window.setTimeout(typeNext, 38);
        return;
      }
      setTypingDone(true);
      leadTimer = window.setTimeout(() => {
        if (cancelled) return;
        setShowLead(true);
        let shown = 0;
        const revealNext = () => {
          if (cancelled) return;
          shown += 1;
          setVisibleCount(shown);
          if (shown < safeCount) {
            revealTimer = window.setTimeout(revealNext, 280);
            return;
          }
          cycleTimer = window.setInterval(() => {
            if (cancelled) return;
            setActiveIndex((prev) => (prev + 1) % safeCount);
          }, 2400);
        };
        revealNext();
      }, 180);
    };

    typeTimer = window.setTimeout(typeNext, 160);

    return () => {
      cancelled = true;
      window.clearTimeout(typeTimer);
      window.clearTimeout(leadTimer);
      window.clearTimeout(revealTimer);
      window.clearInterval(cycleTimer);
    };
  }, [headlineText, safeCount]);

  return { headline, showLead, visibleCount, activeIndex, typingDone };
}

export default function TiktokPlatformGatePage() {
  const { t, i18n } = useTranslation('workbench');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { unlocked, unlock, lock, workspaceShopType } = useTiktokWorkspaceAuth();
  const [shops, setShops] = useState([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [selectedShopType, setSelectedShopType] = useState(
    () => normalizeTiktokShopType(searchParams.get('shopType')) || TIKTOK_SHOP_TYPES[0],
  );
  const [selectedShopId, setSelectedShopId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(() => location.state?.shopAuthError || '');
  const [submitting, setSubmitting] = useState(false);

  const preferredShopId = useMemo(
    () => String(searchParams.get('shopId') || '').trim(),
    [searchParams],
  );

  const gateCopy = useMemo(
    () => {
      void i18n.language;
      return gateCopyForTiktokShopType(selectedShopType);
    },
    [selectedShopType, i18n.language],
  );
  const highlightItems = gateCopy.highlights || [];
  const { headline, showLead, visibleCount, activeIndex, typingDone } = useGateStoryMotion(
    gateCopy.headline,
    highlightItems.length,
  );

  const sessionTypeMatches = Boolean(
    unlocked
    && workspaceShopType
    && normalizeTiktokShopType(workspaceShopType) === normalizeTiktokShopType(selectedShopType),
  );
  const needsPassword = !sessionTypeMatches;

  const shopsOfType = useMemo(
    () => shops.filter(
      (shop) => normalizeTiktokShopType(shop?.shopType) === normalizeTiktokShopType(selectedShopType),
    ),
    [shops, selectedShopType],
  );

  const shopTypeOptions = useMemo(
    () => TIKTOK_SHOP_TYPES.map((type) => ({
      value: type,
      label: localizedShopTypeLabel(t, type),
    })),
    [t],
  );

  const shopOptions = useMemo(() => {
    const options = shopsOfType.map((shop) => ({
      value: String(shop.id),
      label: shopOptionLabel(shop, t),
    }));
    if (shopsOfType.length >= 2) {
      return [
        { value: PLATFORM_ALL_SHOPS_ID, label: t('tiktokGate.allAuthorized') },
        ...options,
      ];
    }
    return options;
  }, [shopsOfType, t]);

  useEffect(() => {
    if (location.state?.shopAuthError) {
      setError(String(location.state.shopAuthError));
    }
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setShopsLoading(true);
      try {
        const list = await listAuthorizedTiktokShops();
        if (cancelled) return;
        setShops(list);
        if (!list.length) {
          setSelectedShopId('');
          setError((prev) => prev || t('shop.enterAuthExpired'));
          return;
        }
        const preferred = preferredShopId
          ? list.find((shop) => String(shop.id) === String(preferredShopId))
          : null;
        if (preferred) {
          const type = normalizeTiktokShopType(preferred.shopType) || TIKTOK_SHOP_TYPES[0];
          setSelectedShopType(type);
          setSelectedShopId(String(preferred.id));
          return;
        }
        const stored = readStoredShopId();
        if (isAllShopsScope(stored)) {
          const preferredType = TIKTOK_SHOP_TYPES[0];
          const preferredShops = list.filter(
            (shop) => normalizeTiktokShopType(shop?.shopType) === preferredType,
          );
          if (preferredShops.length >= 2) {
            setSelectedShopType(preferredType);
            setSelectedShopId(PLATFORM_ALL_SHOPS_ID);
            return;
          }
          for (const type of TIKTOK_SHOP_TYPES) {
            const ofType = list.filter(
              (shop) => normalizeTiktokShopType(shop?.shopType) === type,
            );
            if (ofType.length >= 2) {
              setSelectedShopType(type);
              setSelectedShopId(PLATFORM_ALL_SHOPS_ID);
              return;
            }
          }
        }
        const storedShop = list.find((shop) => String(shop.id) === String(stored));
        if (storedShop) {
          setSelectedShopType(normalizeTiktokShopType(storedShop.shopType) || TIKTOK_SHOP_TYPES[0]);
          setSelectedShopId(String(storedShop.id));
        }
      } catch {
        if (!cancelled) {
          setShops([]);
          setError(t('shop.enterAuthExpired'));
        }
      } finally {
        if (!cancelled) setShopsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [preferredShopId, t]);

  useEffect(() => {
    if (!selectedShopId) return;
    if (isAllShopsScope(selectedShopId)) {
      if (shopsOfType.length < 2) setSelectedShopId('');
      return;
    }
    const stillVisible = shopsOfType.some((shop) => String(shop.id) === String(selectedShopId));
    if (!stillVisible) setSelectedShopId('');
  }, [shopsOfType, selectedShopId]);

  const applySelectedShop = async (shopId) => {
    const id = String(shopId || '').trim();
    if (!id) return;
    persistActiveShopId(id);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!selectedShopType) {
      setError(t('shop.needShopType'));
      return;
    }
    if (!selectedShopId) {
      setError(t('tiktokGate.needShop'));
      return;
    }
    const enteringAll = isAllShopsScope(selectedShopId);
    if (enteringAll) {
      if (shopsOfType.length < 2) {
        setError(t('tiktokGate.needTwoShops'));
        return;
      }
    } else {
      const selected = shops.find((shop) => String(shop.id) === String(selectedShopId));
      if (!selected
        || normalizeTiktokShopType(selected.shopType) !== normalizeTiktokShopType(selectedShopType)) {
        setError(t('tiktokGate.shopUnavailable'));
        return;
      }
    }
    if (needsPassword && !password.trim()) {
      setError(unlocked ? t('tiktokGate.needPasswordSwitch') : t('tiktokGate.needPassword'));
      return;
    }
    setSubmitting(true);
    try {
      if (needsPassword) {
        await unlock(password, selectedShopType);
      }
      const list = await listAuthorizedTiktokShops({ shopType: selectedShopType });
      if (enteringAll) {
        if (list.length < 2) {
          await lock();
          setShops(list);
          setSelectedShopId('');
          setError(t('shop.enterAuthExpired'));
          return;
        }
      } else {
        const stillOk = list.some((shop) => String(shop.id) === String(selectedShopId));
        if (!stillOk) {
          await lock();
          setShops(list);
          setSelectedShopId('');
          setError(t('shop.enterAuthExpired'));
          return;
        }
      }
      await applySelectedShop(selectedShopId);
      navigate(resolveRedirect(searchParams, location.state, selectedShopType), { replace: true });
    } catch (err) {
      const msg = err?.code === 'TIKTOK_PASSWORD_INVALID'
        ? t('tiktokGate.wrongPassword')
        : (err?.message || t('tiktokGate.passwordFailed'));
      setError(msg);
      if (needsPassword) setPassword('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    window.location.href = `${IPE_WORKBENCH_PATH}?activeNav=sheinHub`;
  };

  return (
    <div className="tiktok-gate">
      <div
        className="tiktok-gate__hero"
        style={{ backgroundImage: `url(${GATE_HERO})` }}
        aria-hidden="true"
      />
      <div className="tiktok-gate__veil" aria-hidden="true" />
      <div className="tiktok-gate__stage">
        <section className="tiktok-gate__story" aria-live="polite">
          <p className="tiktok-gate__kicker">IPE × TikTok · {localizedShopTypeLabel(t, selectedShopType)}</p>
          <h1 className="tiktok-gate__brand">IPE</h1>
          <p className={`tiktok-gate__headline${typingDone ? ' is-done' : ' is-typing'}`}>
            <span>{headline}</span>
            {!typingDone ? <i className="tiktok-gate__caret" aria-hidden="true" /> : null}
          </p>
          <p className={`tiktok-gate__lead${showLead ? ' is-visible' : ''}`}>
            {gateCopy.lead}
          </p>
          <ul className="tiktok-gate__highlights">
            {highlightItems.map((item, index) => {
              const visible = index < visibleCount;
              const active = visible && index === activeIndex && visibleCount >= highlightItems.length;
              return (
                <li
                  key={`${selectedShopType}-${item.title}`}
                  className={`tiktok-gate__highlight${visible ? ' is-visible' : ''}${active ? ' is-active' : ''}`}
                >
                  <span className="tiktok-gate__highlight-mark" aria-hidden="true" />
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.desc}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <aside className="tiktok-gate__auth" aria-label={t('tiktokGate.authAria')}>
          <div className="tiktok-gate__auth-glow" aria-hidden="true" />
          <div className="tiktok-gate__panel">
            <div className="tiktok-gate__header">
              <span className="tiktok-gate__brand-badge">TikTok</span>
              <h2>{t('tiktokGate.title')}</h2>
            </div>
            <p className="tiktok-gate__panel-desc">
              {t('tiktokGate.panelLead')}
            </p>

            <form className="tiktok-gate__form" onSubmit={handleSubmit} autoComplete="off">
              {/* 诱饵字段：承接浏览器账号密码自动填充，避免写入店铺搜索框 */}
              <div className="tiktok-gate__autofill-trap" aria-hidden="true">
                <input type="text" name="username" autoComplete="username" tabIndex={-1} defaultValue="" />
                <input type="password" name="password" autoComplete="current-password" tabIndex={-1} defaultValue="" />
              </div>
              <label className="tiktok-gate__field">
                <span>{t('shop.shopType')}</span>
                <span className="tiktok-gate__input-wrap tiktok-gate__input-wrap--select">
                  <Tags size={16} aria-hidden="true" />
                  <TiktokGateSelect
                    value={selectedShopType}
                    options={shopTypeOptions}
                    disabled={shopsLoading || submitting}
                    placeholder={t('tiktokGate.shopTypePlaceholder')}
                    onChange={(next) => {
                      setSelectedShopType(next);
                      setSelectedShopId('');
                      setError('');
                    }}
                  />
                </span>
              </label>

              <div className="tiktok-gate__field">
                <span>{t('tiktokGate.loginShop')}</span>
                <span
                  className="tiktok-gate__input-wrap tiktok-gate__input-wrap--select"
                  onMouseDown={(e) => {
                    if (e.target?.closest?.('.tiktok-gate-select__input')) return;
                    if (e.target?.closest?.('.tiktok-gate-select__clear')) return;
                    const input = e.currentTarget.querySelector('input.tiktok-gate-select__input');
                    if (!input || input.disabled) return;
                    e.preventDefault();
                    input.focus();
                  }}
                >
                  <Store size={16} aria-hidden="true" />
                  <TiktokGateSelect
                    value={selectedShopId}
                    options={shopOptions}
                    disabled={shopsLoading || submitting || shops.length === 0}
                    placeholder={shopsLoading ? t('tiktokGate.loadingShops') : t('tiktokGate.shopSearchPlaceholder')}
                    emptyText={t('tiktokGate.noShops')}
                    searchable
                    allowClear
                    inputName="tiktok-gate-shop"
                    onChange={(next) => setSelectedShopId(next)}
                  />
                </span>
              </div>

              {needsPassword ? (
                <label className="tiktok-gate__field">
                  <span>{t('tiktokGate.passwordLabel')}</span>
                  <span className="tiktok-gate__input-wrap tiktok-gate__input-wrap--password">
                    <Lock size={16} aria-hidden="true" />
                    <input
                      id="tiktok-gate-password"
                      name="tiktok-gate-ipe-verify"
                      className="tiktok-gate__password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('tiktokGate.passwordPlaceholder')}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      data-form-type="other"
                      required
                    />
                    <button
                      type="button"
                      className="tiktok-gate__password-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? t('tiktokGate.hidePassword') : t('tiktokGate.showPassword')}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </span>
                </label>
              ) : (
                <p className="tiktok-gate__session-hint">{t('tiktokGate.sessionHint')}</p>
              )}

              {error ? <p className="tiktok-gate__error">{error}</p> : null}

              <div className="tiktok-gate__actions">
                <button
                  type="button"
                  className="tiktok-gate__btn tiktok-gate__btn--ghost"
                  onClick={handleCancel}
                >
                  {t('tiktokGate.backWorkbench')}
                </button>
                <button
                  type="submit"
                  className="tiktok-gate__btn tiktok-gate__btn--primary"
                  disabled={submitting || shopsLoading || shops.length === 0}
                >
                  {submitting ? t('tiktokGate.entering') : t('tiktokGate.confirmEnter')}
                </button>
              </div>
            </form>
          </div>
        </aside>
      </div>
    </div>
  );
}
